/**
 * HieRecordsPanel — Metriport HIE records surfaced inside the intake drawer (iter 171).
 *
 * Sections: H&Ps · Diagnoses · Medications · Labs · Encounters ·
 *           Prior Hospitalizations · Allergies · Immunizations.
 *
 * The parent (`AdminIntakeInbox`) owns the axios calls for pull start/refresh
 * — this panel is fully presentational and just renders the categorized
 * `summary` payload from `GET /api/hie/pull/{intake_id}`.
 *
 * Testids:
 *   hie-panel · hie-status · hie-refresh-btn · hie-empty ·
 *   hie-count-hnps · hie-count-diagnoses · hie-count-medications ·
 *   hie-count-labs · hie-count-encounters · hie-count-prior-hospitalizations ·
 *   hie-section-<key> · hie-row-<key>-<id>
 */
import React, { useState } from 'react';
import {
  RefreshCw, ShieldCheck, Stethoscope, Pill, FlaskConical, CalendarClock,
  Hospital, AlertTriangle, Syringe, FileText, Loader2, ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function StatusPill({ status, mode }) {
  const STYLE = {
    completed:  'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    processing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    failed:     'bg-rose-500/15 text-rose-300 border-rose-500/30',
    idle:       'bg-slate-500/15 text-slate-400 border-slate-500/30',
  }[status] || 'bg-slate-500/15 text-slate-400 border-slate-500/30';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${STYLE}`}
      data-testid="hie-status"
    >
      <ShieldCheck className="h-3 w-3" />
      HIE {status}
      {mode === 'mock' && (
        <span className="ml-1 uppercase text-[9px] tracking-wider bg-black/40 rounded px-1">MOCK</span>
      )}
    </span>
  );
}

function CountTile({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-semibold tabular-nums text-slate-100 mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function CollapseSection({ title, icon: Icon, testId, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden" data-testid={testId}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-800/40"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
          <Icon className="h-4 w-4 text-slate-400" /> {title}
          <span className="ml-2 text-xs font-normal text-slate-500">({count})</span>
        </span>
        <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-slate-800 p-3 space-y-2">{children}</div>
      )}
    </section>
  );
}

function EmptyRow({ label }) {
  return <p className="text-xs text-slate-500 italic px-1 py-1">{label}</p>;
}

export function HieRecordsPanel({ pull, onRefresh, canConsent, hasConsent, onCaptureConsent, busy }) {
  const status = pull?.status || 'idle';
  const mode = pull?.mode;
  const summary = pull?.summary || {};
  const counts = summary.counts || {};

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-4 space-y-4" data-testid="hie-panel">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-300">
            <ShieldCheck className="h-3.5 w-3.5 text-cn-primary" /> HIE records (Metriport)
          </h3>
          <StatusPill status={status} mode={mode} />
        </div>
        <div className="flex items-center gap-2">
          {canConsent && !hasConsent && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCaptureConsent}
              disabled={busy}
              data-testid="hie-capture-consent"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Capture consent
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={busy || (!hasConsent && status === 'idle')}
            data-testid="hie-refresh-btn"
            title={!hasConsent ? 'Capture patient consent first' : ''}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            {status === 'idle' || status === 'failed' ? 'Pull HIE records' : 'Refresh'}
          </Button>
        </div>
      </header>

      {status === 'idle' && (
        <p className="text-xs text-slate-500 italic" data-testid="hie-empty">
          No HIE pull yet. Capture patient consent and press <em>Pull HIE records</em> to query CommonWell + Carequality.
        </p>
      )}

      {status === 'failed' && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          HIE pull failed{pull?.error ? `: ${pull.error}` : '.'} Try Refresh — check credentials if this keeps happening.
        </div>
      )}

      {status === 'processing' && (
        <p className="text-xs text-amber-300 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Querying HIE networks — typically 30-60s in live mode.
        </p>
      )}

      {status === 'completed' && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <CountTile icon={FileText}       label="H&Ps"     value={counts.hnps}                     testId="hie-count-hnps" />
            <CountTile icon={Stethoscope}    label="Diagnoses" value={counts.diagnoses}               testId="hie-count-diagnoses" />
            <CountTile icon={Pill}           label="Meds"     value={counts.medications}              testId="hie-count-medications" />
            <CountTile icon={FlaskConical}   label="Labs"     value={counts.labs}                     testId="hie-count-labs" />
            <CountTile icon={CalendarClock}  label="Encntrs"  value={counts.encounters}               testId="hie-count-encounters" />
            <CountTile icon={Hospital}       label="Prior Hosp" value={counts.prior_hospitalizations} testId="hie-count-prior-hospitalizations" />
          </div>

          <CollapseSection title="H&Ps"       icon={FileText}     testId="hie-section-hnps"     count={counts.hnps} defaultOpen>
            {(summary.hnps || []).length === 0 ? <EmptyRow label="No H&P documents returned." /> :
              summary.hnps.map((h) => (
                <div key={h.id} className="rounded-md border border-slate-800 bg-slate-900/50 p-3" data-testid={`hie-row-hnps-${h.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-100">{h.title}</div>
                    <div className="text-[10px] text-slate-500">{fmtDate(h.date)}</div>
                  </div>
                  {h.author && <div className="text-[11px] text-slate-500">{h.author}</div>}
                  {h.snippet && <p className="mt-1 text-xs text-slate-300 whitespace-pre-wrap">{h.snippet}</p>}
                </div>
              ))
            }
          </CollapseSection>

          <CollapseSection title="Diagnoses" icon={Stethoscope} testId="hie-section-diagnoses" count={counts.diagnoses}>
            {(summary.diagnoses || []).length === 0 ? <EmptyRow label="No diagnoses returned." /> :
              summary.diagnoses.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-xs text-slate-200 py-1 border-b border-slate-800/60 last:border-0"
                     data-testid={`hie-row-diagnoses-${d.id}`}>
                  <span className="font-mono text-slate-400 w-16">{d.code || '—'}</span>
                  <span className="flex-1">{d.display || '(unspecified)'}</span>
                  <span className={`text-[10px] uppercase tracking-wide ${d.status === 'active' ? 'text-emerald-300' : 'text-slate-500'}`}>{d.status || ''}</span>
                  <span className="text-[10px] text-slate-500">{fmtDate(d.onset)}</span>
                </div>
              ))
            }
          </CollapseSection>

          <CollapseSection title="Medications" icon={Pill} testId="hie-section-medications" count={counts.medications}>
            {(summary.medications || []).length === 0 ? <EmptyRow label="No medications returned." /> :
              summary.medications.map((m) => (
                <div key={m.id} className="flex items-center gap-2 text-xs text-slate-200 py-1 border-b border-slate-800/60 last:border-0"
                     data-testid={`hie-row-medications-${m.id}`}>
                  <span className="flex-1 font-medium">{m.name || '(no name)'}</span>
                  {m.reason && <span className="text-slate-500 italic">{m.reason}</span>}
                  <span className="text-[10px] text-slate-500">since {fmtDate(m.since)}</span>
                </div>
              ))
            }
          </CollapseSection>

          <CollapseSection title="Labs" icon={FlaskConical} testId="hie-section-labs" count={counts.labs}>
            {(summary.labs || []).length === 0 ? <EmptyRow label="No labs returned." /> :
              summary.labs.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-xs text-slate-200 py-1 border-b border-slate-800/60 last:border-0"
                     data-testid={`hie-row-labs-${l.id}`}>
                  <span className="flex-1">{l.name || '(unspecified)'}</span>
                  <span className="tabular-nums font-medium">{l.value ?? '—'} <span className="text-slate-500">{l.unit || ''}</span></span>
                  <span className="text-[10px] text-slate-500">{fmtDate(l.when)}</span>
                </div>
              ))
            }
          </CollapseSection>

          <CollapseSection title="Encounters" icon={CalendarClock} testId="hie-section-encounters" count={counts.encounters}>
            {(summary.encounters || []).length === 0 ? <EmptyRow label="No outpatient encounters returned." /> :
              summary.encounters.map((e) => (
                <div key={e.id} className="rounded-md border border-slate-800 bg-slate-900/50 p-2 text-xs" data-testid={`hie-row-encounters-${e.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-100">{e.reason || e.kind || 'Encounter'}</span>
                    <span className="text-[10px] text-slate-500">{fmtDate(e.start)}</span>
                  </div>
                  {e.facility && <div className="text-[11px] text-slate-500">{e.facility}</div>}
                </div>
              ))
            }
          </CollapseSection>

          <CollapseSection title="Prior hospitalizations" icon={Hospital} testId="hie-section-prior-hospitalizations" count={counts.prior_hospitalizations} defaultOpen>
            {(summary.prior_hospitalizations || []).length === 0 ? <EmptyRow label="No inpatient stays returned." /> :
              summary.prior_hospitalizations.map((h) => (
                <div key={h.id} className="rounded-md border border-slate-800 bg-slate-900/50 p-3" data-testid={`hie-row-prior-hospitalizations-${h.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-100">{h.reason || 'Hospitalization'}</div>
                    {h.los_days != null && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                        LOS {h.los_days}d
                      </span>
                    )}
                  </div>
                  {h.facility && <div className="text-[11px] text-slate-500">{h.facility}</div>}
                  <div className="text-[11px] text-slate-500 mt-1">
                    Admitted {fmtDate(h.admitted_at)} · Discharged {fmtDate(h.discharged_at)}
                  </div>
                </div>
              ))
            }
          </CollapseSection>

          {(counts.allergies > 0) && (
            <CollapseSection title="Allergies" icon={AlertTriangle} testId="hie-section-allergies" count={counts.allergies}>
              {(summary.allergies || []).map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-xs text-slate-200 py-1 border-b border-slate-800/60 last:border-0"
                     data-testid={`hie-row-allergies-${a.id}`}>
                  <span className="flex-1 font-medium">{a.substance}</span>
                  {a.reaction && <span className="text-slate-500 italic">{a.reaction}</span>}
                  {a.severity && (
                    <span className={`text-[10px] uppercase tracking-wide ${a.severity === 'high' ? 'text-rose-300' : 'text-amber-300'}`}>{a.severity}</span>
                  )}
                </div>
              ))}
            </CollapseSection>
          )}

          {(counts.immunizations > 0) && (
            <CollapseSection title="Immunizations" icon={Syringe} testId="hie-section-immunizations" count={counts.immunizations}>
              {(summary.immunizations || []).map((i) => (
                <div key={i.id} className="flex items-center gap-2 text-xs text-slate-200 py-1 border-b border-slate-800/60 last:border-0"
                     data-testid={`hie-row-immunizations-${i.id}`}>
                  <span className="flex-1">{i.vaccine}</span>
                  <span className="text-[10px] text-slate-500">{fmtDate(i.given_at)}</span>
                </div>
              ))}
            </CollapseSection>
          )}

          {pull.completed_at && (
            <p className="text-[10px] text-slate-500 italic">
              Last refreshed {new Date(pull.completed_at).toLocaleString()} ({mode || 'live'} mode).
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default HieRecordsPanel;
